import React from 'react';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import UploadIcon from '@mui/icons-material/Upload';
import DownloadIcon from '@mui/icons-material/Download';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { ProcessedParcel } from './types';
import { ProgressRing } from './ProgressRing';
import { StageChip, StageTone } from './StageChip';
import { StatusBadge } from './StatusBadge';
import { APCard } from './APCard';
import { EmptyAPState } from './EmptyAPState';
import {
  computeProgress,
  formatRelativeWeek,
  formatWeekTitle,
  isCurrentWeek,
} from './utils';
import { useCustomer, CustomerStage } from '../../customer';

const STAGE_WIDTHS: Record<CustomerStage, string> = {
  // Wider than the other stages: these chips carry a file count as well as the carrier.
  ups: '150px',
  gls: '150px',
  fbo: '150px',
  dhl: '150px',
  invoice: '118px',
  remits: '104px',
};

const formatRunTime = (iso: string): string => {
  const when = new Date(iso);
  if (Number.isNaN(when.getTime())) return 'at an unknown time';
  const time = when.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  return new Date().toDateString() === when.toDateString()
    ? `today ${time}`
    : `${when.toLocaleDateString(undefined, { weekday: 'short', month: 'numeric', day: 'numeric' })}, ${time}`;
};

// The upload chip stays a link once files are in: a wrong or incomplete upload is fixed by
// going back to the page, not by resetting a status first. The count is what tells them
// whether what is there is what they meant to send.
const uploadChip = (
  label: string,
  uploaded: boolean,
  count: number | null,
  to: string,
  noun: string
) => ({
  // A zero count says nothing a chip reading "Upload UPS" has not already said, and reads as
  // though something went wrong. Only a real count earns the suffix.
  label: !count ? label : `${label} · ${count} file${count === 1 ? '' : 's'}`,
  icon: uploaded ? undefined : <UploadIcon />,
  to,
  tooltip: uploaded
    ? `${noun} uploaded — open to view, add more or remove`
    : `Upload ${noun} for this week`,
});

export type WeekCardProps = {
  row: ProcessedParcel;
  expanded: boolean;
  onToggleExpand: (rowId: string) => void;
  onRequestToggleInvoice: (rowId: string, currentValue: boolean) => void;
  onRequestToggleRemit: (rowId: string, apIndex: number, currentValue: boolean) => void;
};

export const WeekCard = ({
  row,
  expanded,
  onToggleExpand,
  onRequestToggleInvoice,
  onRequestToggleRemit,
}: WeekCardProps) => {
  const { customer } = useCustomer();
  const stages = customer.stages;

  const showUPS = stages.includes('ups');
  const showGLS = stages.includes('gls');
  const showFBO = stages.includes('fbo');
  const showDHL = stages.includes('dhl');
  const showInvoice = stages.includes('invoice');
  const showRemits = stages.includes('remits');

  const progress = computeProgress(row, stages);
  const title = formatWeekTitle(row.Week);
  const relative = formatRelativeWeek(row.Week);
  const aps = row.AccountsPayable ?? [];

  const ringTone: 'success' | 'warning' | 'danger' | 'neutral' =
    progress.percent >= 100 ? 'success'
      : progress.percent === 0 ? 'neutral'
      : 'warning';

  const weekParam = encodeURIComponent(row.Week || '');

  const lastRun = row.LastRunAt
    ? {
        text: `${row.LastRunTrigger === 'manual' ? 'Rerun' : 'Last run'} ${formatRunTime(
          row.LastRunAt
        )}${row.LastRunOutcome ? ` — ${row.LastRunOutcome}` : ''}`,
        tone: row.LastRunOutcome && row.LastRunOutcome.startsWith('No files')
          ? 'warning.main'
          : 'text.secondary',
      }
    // No record means the week ran before runs were recorded, not that it never ran. Saying
    // anything here would be a guess, so the row is simply absent.
    : null;

  const upsTone: StageTone = row.UPSImportUploaded ? 'done' : 'action';
  const upsConfig = uploadChip(
    row.UPSImportUploaded ? 'UPS' : 'Upload UPS',
    row.UPSImportUploaded,
    row.UPSImportFileCount,
    `/ups-import-upload?week=${weekParam}`,
    'UPS import'
  );

  const glsTone: StageTone = row.GLSImportUploaded ? 'done' : 'action';
  const glsConfig = uploadChip(
    row.GLSImportUploaded ? 'GLS' : 'Upload GLS',
    row.GLSImportUploaded,
    row.GLSImportFileCount,
    `/gls-import-upload?week=${weekParam}`,
    'GLS import'
  );

  const fboTone: StageTone = row.FBOImportUploaded ? 'done' : 'action';
  const fboConfig = uploadChip(
    row.FBOImportUploaded ? 'FBO' : 'Upload FBO',
    row.FBOImportUploaded,
    row.FBOImportFileCount,
    `/fbo-import-upload?week=${weekParam}`,
    'FedEx FBO import'
  );

  const dhlTone: StageTone = row.DHLImportUploaded ? 'done' : 'action';
  const dhlConfig = uploadChip(
    row.DHLImportUploaded ? 'DHL' : 'Upload DHL',
    row.DHLImportUploaded,
    row.DHLImportFileCount,
    `/dhl-import-upload?week=${weekParam}`,
    'DHL import'
  );

  const invoiceHasFile = Boolean(row.InvoiceImportFileUrl);
  const invoiceCanToggle = invoiceHasFile || row.InvoiceImportUploaded === true;
  const invoiceTone: StageTone = row.InvoiceImportUploaded
    ? 'done'
    : invoiceCanToggle
      ? 'action'
      : 'idle';

  const remitsTotal = progress.remitsTotal;
  const remitsUploaded = progress.remitsUploaded;
  let remitsTone: StageTone;
  let remitsLabel: string;
  let remitsIcon: React.ReactNode;

  if (remitsTotal === 0) {
    remitsTone = 'idle';
    remitsLabel = 'Remits';
    remitsIcon = null;
  } else {
    remitsTone = remitsUploaded === remitsTotal ? 'done' : 'action';
    remitsLabel = `${remitsUploaded} / ${remitsTotal} Remits`;
    remitsIcon = null;
  }

  const isCurrent = isCurrentWeek(row.Week);
  const insetAccent = (t: any) =>
    isCurrent ? `inset 4px 0 0 ${t.palette.primary.main}` : '';

  const composeShadow = (t: any, ...layers: string[]): string =>
    [insetAccent(t), ...layers].filter(Boolean).join(', ') || 'none';

  const cardBorderSx = expanded
    ? {
        borderColor: 'primary.main',
        boxShadow: (t: any) =>
          composeShadow(t, `0 0 0 3px ${t.palette.primary.light}`),
      }
    : {
        borderColor: 'divider',
        boxShadow: (t: any) => composeShadow(t),
        '&:hover': {
          borderColor: 'grey.300',
          boxShadow: (t: any) =>
            composeShadow(t, '0 4px 12px rgba(15, 23, 42, 0.06)'),
        },
      };

  const apCount = aps.length;
  const subBits = [row.Week, relative];
  if (showRemits) {
    subBits.push(
      apCount > 0
        ? `${apCount} FedEx ${apCount === 1 ? 'account' : 'accounts'}`
        : 'No FedEx accounts'
    );
  }

  const allBaseStagesDone = stages
    .filter((s) => s !== 'remits')
    .every((s) => {
      if (s === 'ups') return row.UPSImportUploaded;
      if (s === 'gls') return row.GLSImportUploaded;
      if (s === 'fbo') return row.FBOImportUploaded;
      if (s === 'dhl') return row.DHLImportUploaded;
      if (s === 'invoice') return row.InvoiceImportUploaded;
      return false;
    });
  const isAwaitingRemits =
    showRemits && allBaseStagesDone && remitsTotal === 0;

  let statusTone: 'complete' | 'pending' | 'idle';
  let statusLabel: string;
  if (progress.isComplete) {
    statusTone = 'complete';
    statusLabel = 'Complete';
  } else if (isAwaitingRemits || progress.percent === 0) {
    statusTone = 'idle';
    statusLabel = 'Awaiting';
  } else {
    statusTone = 'pending';
    statusLabel = 'In Progress';
  }

  return (
    <Box
      sx={{
        backgroundColor: 'background.paper',
        border: '1px solid',
        borderRadius: 2,
        transition: 'box-shadow 0.15s ease, border-color 0.15s ease',
        overflow: 'hidden',
        ...cardBorderSx,
      }}
    >
      {/* MAIN ROW */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: 'auto 1fr auto',
            md: showRemits
              ? 'auto minmax(180px, 1fr) auto auto auto'
              : 'auto minmax(180px, 1fr) auto auto',
          },
          gap: 2,
          alignItems: 'center',
          px: { xs: 2, md: 2.5 },
          py: 1.5,
        }}
      >
        <ProgressRing
          percent={progress.percent}
          tone={ringTone}
          label={`${progress.done} of ${progress.total} stages complete`}
        />

        <Box>
          <Typography sx={{ fontWeight: 600, fontSize: 15, letterSpacing: '-0.1px' }}>
            {title}
          </Typography>
          <Typography
            sx={{ color: 'text.disabled', fontSize: 12, mt: 0.25 }}
          >
            {subBits.join(' · ')}
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs:
                stages.length <= 2
                  ? `repeat(${stages.length}, 1fr)`
                  : 'repeat(2, 1fr)',
              md: stages.map((s) => STAGE_WIDTHS[s]).join(' '),
            },
            gap: 1,
            gridColumn: { xs: '1 / -1', md: 'auto' },
          }}
        >
          {showUPS && (
            <StageChip
              tone={upsTone}
              label={upsConfig.label}
              icon={upsConfig.icon}
              to={upsConfig.to}
              tooltip={upsConfig.tooltip}
            />
          )}
          {showGLS && (
            <StageChip
              tone={glsTone}
              label={glsConfig.label}
              icon={glsConfig.icon}
              to={glsConfig.to}
              tooltip={glsConfig.tooltip}
            />
          )}
          {showFBO && (
            <StageChip
              tone={fboTone}
              label={fboConfig.label}
              icon={fboConfig.icon}
              to={fboConfig.to}
              tooltip={fboConfig.tooltip}
            />
          )}
          {showDHL && (
            <StageChip
              tone={dhlTone}
              label={dhlConfig.label}
              icon={dhlConfig.icon}
              to={dhlConfig.to}
              tooltip={dhlConfig.tooltip}
            />
          )}
          {showInvoice && (
            <StageChip
              tone={invoiceTone}
              label="Invoice"
              disabled={!invoiceCanToggle}
              onClick={
                invoiceCanToggle
                  ? () => onRequestToggleInvoice(row.id, row.InvoiceImportUploaded === true)
                  : undefined
              }
              tooltip={
                !invoiceCanToggle
                  ? 'Invoice file not yet available — cannot mark as uploaded'
                  : row.InvoiceImportUploaded
                    ? 'Invoice uploaded — click to mark as not uploaded'
                    : 'Click to mark invoice as uploaded'
              }
              aux={invoiceHasFile ? {
                icon: <DownloadIcon />,
                tooltip: 'Download invoice import file',
                ariaLabel: 'Download invoice import file',
                onClick: () => window.open(row.InvoiceImportFileUrl, '_blank', 'noopener,noreferrer'),
              } : undefined}
            />
          )}
          {showRemits && (
            <StageChip
              tone={remitsTone}
              label={remitsLabel}
              icon={remitsIcon}
              disabled={remitsTotal === 0}
              onClick={remitsTotal > 0 ? () => onToggleExpand(row.id) : undefined}
              tooltip={
                remitsTotal > 0
                  ? 'Expand to view FedEx remits'
                  : 'FedEx remits not yet generated for this week'
              }
            />
          )}
        </Box>

        <Box sx={{ width: { md: 112 }, flexShrink: 0, display: 'flex' }}>
          <StatusBadge tone={statusTone} label={statusLabel} stretch />
        </Box>

        {showRemits && (
          <IconButton
            onClick={() => onToggleExpand(row.id)}
            aria-label={expanded ? 'Collapse' : 'Expand'}
            size="small"
            sx={{
              width: 32,
              height: 32,
              color: expanded ? 'primary.main' : 'text.secondary',
              backgroundColor: expanded ? 'primary.light' : 'transparent',
              transition: 'transform 0.2s ease, background 0.15s ease',
              transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
              '&:hover': {
                backgroundColor: expanded ? 'primary.light' : 'grey.100',
              },
            }}
          >
            <ExpandMoreIcon />
          </IconButton>
        )}
      </Box>

      {/* What the last run actually did — the distinction a rerun turns on, since "ran and found
          nothing" and "never ran" leave the stages looking identical. */}
      {lastRun && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            // Matches the main row's own padding; the card root carries none of its own.
            px: { xs: 2, md: 2.5 },
            py: 1.25,
            borderTop: '1px solid',
            borderColor: 'grey.100',
          }}
        >
          <Box
            sx={{
              width: 7,
              height: 7,
              borderRadius: '50%',
              flexShrink: 0,
              backgroundColor: lastRun.tone === 'warning.main' ? 'warning.main' : 'grey.400',
            }}
          />
          <Typography variant="body2" sx={{ color: lastRun.tone }}>
            {lastRun.text}
          </Typography>
        </Box>
      )}

      {/* EXPANDED PANEL — FedEx AP detail; only relevant when remits are enabled */}
      <Collapse in={showRemits && expanded} timeout="auto" unmountOnExit>
        <Box
          sx={{
            borderTop: '1px solid',
            borderColor: 'divider',
            background: (t: any) => `linear-gradient(180deg, ${t.palette.grey[50]} 0%, ${t.palette.grey[100]} 100%)`,
            px: { xs: 2, md: 3 },
            py: 2,
            pl: { xs: 2, md: '78px' },
          }}
        >
          {aps.length > 0 ? (
            <>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.25,
                  color: 'text.secondary',
                  fontSize: 11.5,
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '0.8px',
                  mb: 1.5,
                }}
              >
                <LocalShippingIcon sx={{ fontSize: 16 }} />
                FedEx Accounts Payable · {aps.length} {aps.length === 1 ? 'account' : 'accounts'}
                <Box sx={{ flex: 1, height: '1px', backgroundColor: 'divider' }} />
              </Box>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
                  gap: 1.5,
                }}
              >
                {aps.map((ap, idx) => (
                  <APCard
                    key={idx}
                    rowId={row.id}
                    week={row.Week}
                    index={idx}
                    item={ap}
                    onRequestToggleRemit={onRequestToggleRemit}
                  />
                ))}
              </Box>
            </>
          ) : (
            <EmptyAPState />
          )}
        </Box>
      </Collapse>
    </Box>
  );
};
